import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { appBarHeight, navBarHeight } from '../../Constants';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import type Vessel from '../../Models/VesselModel';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import AddNewVesselDialog from '../../Dialogs/VesselDialogs/AddNewVesselDialog';
import ExistingVesselInfoDialog from '../../Dialogs/VesselDialogs/ExistingVesselInfoDialog';
import { LoadDataBackdrop } from 'shouldve-been-mui';
import { updateLastActivity } from '../../Utils/LastActivity';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import type VesselType from '../../Models/VesselTypeModel';
import Company from '../../Models/CompanyModel';
import { shipNewToVisualString } from '../../Utils/VesselFunctions';
import type { RunContextProps } from '../../Contexts/RunContext';
import RunContext from '../../Contexts/RunContext';

function VesselsTab(): JSX.Element {

    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const runContext: RunContextProps = useContext<RunContextProps>(RunContext);
    
    const currentTheme: Theme = useTheme();

    const [existingVessels, setExistingVessels] = useState<Vessel[]>([]);
    const [vesselTypes, setVesselTypes] = useState<VesselType[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);

    const [vesselsIsLoaded, setVesselsIsLoaded] = useState<boolean>(false);
    const [vesselTypesIsLoaded, setVesselTypesIsLoaded] = useState<boolean>(false);
    const [companiesIsLoaded, setCompaniesIsLoaded] = useState<boolean>(false);

    const [showAddNewVesselDialog, setShowAddNewVesselDialog] = useState<boolean>(false);
    const [showExistingVesselInfoDialog, setShowExistingVesselInfoDialog] = useState<Vessel | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [rerenderFlag, setRerenderFlag] = useState<Date>(new Date());

    const sortedExistingVessels: Vessel[] = useMemo<Vessel[]>(() => [...existingVessels].sort((a: Vessel, b: Vessel) => (a.sortNo ?? 0) - (b.sortNo ?? 0)), [existingVessels]);

    const tableBody: JSX.Element[] = useMemo<JSX.Element[]>(() => sortedExistingVessels.map((vessel: Vessel) => {

        return (
            <TableRow
                key={vessel.vesselId}
                onClick={(): void => setShowExistingVesselInfoDialog(vessel)}
                style={{
                    cursor: 'pointer',
                }}
            >
                <TableCell>
                    {vessel.vesselName}
                </TableCell>
                <TableCell>
                    {vessel.imoNumber}
                </TableCell>
                <TableCell>
                    {companies.find((company: Company) => company.companyNo === vessel.companyNo)?.name ?? ''}
                </TableCell>
                <TableCell>
                    {vesselTypes.find((vesselType: VesselType) => vesselType.vesselTypeId === vessel.vesselTypeId)?.vesselType ?? ''}
                </TableCell>
                <TableCell>
                    {shipNewToVisualString(vessel.shipNew)}
                </TableCell>
            </TableRow>
        );
    }), [companies, sortedExistingVessels, vesselTypes]);
    
    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        runContext.setTitle('K-Fleet Vessel Manager');
    }, [runContext]);

    useEffect(() => { //vessels

        let mounted: boolean = true;

        async function fetchVessels(): Promise<void> {

            setVesselsIsLoaded(false);
            
            const newVessels: Vessel[] = await apiContext.api?.getVessels(tokenContext.token) ?? [];

            if (!mounted) {
                return;
            }

            setExistingVessels(newVessels);

            setVesselsIsLoaded(true);
        }

        void fetchVessels();

        return () => {
            mounted = false;
        };

    }, [apiContext.api, tokenContext.token, rerenderFlag]);

    useEffect(() => { //vessel types

        let mounted: boolean = true;

        async function fetchVesselTypes(): Promise<void> {

            setVesselTypesIsLoaded(false);
            
            const newVesselTypes: VesselType[] = await apiContext.api?.getVesselTypes(tokenContext.token) ?? [];

            if (!mounted) {
                return;
            }

            setVesselTypes(newVesselTypes);

            setVesselTypesIsLoaded(true);
        }

        void fetchVesselTypes();

        return () => {
            mounted = false;
        };

    }, [apiContext.api, tokenContext.token, rerenderFlag]);

    useEffect(() => { //companies

        let mounted: boolean = true;

        async function fetchCompanies(): Promise<void> {

            setCompaniesIsLoaded(false);
            
            const newCompanies: Company[] = await apiContext.api?.getCompanies(tokenContext.token) ?? [];

            //add the special "new vessel company"
            newCompanies.push(new Company(9999, 9999, '<New Vessel Company>', null, null, null, null, null, null));

            if (!mounted) {
                return;
            }

            setCompanies(newCompanies);

            setCompaniesIsLoaded(true);
        }

        void fetchCompanies();

        return () => {
            mounted = false;
        };

    }, [apiContext.api, tokenContext.token, rerenderFlag]);

    function handleCloseAddNewVesselDialog(success: boolean): void {

        updateLastActivity(userContext, localStorageContext);

        setShowAddNewVesselDialog(false);

        if (success) {
            setRerenderFlag(new Date());
        }
    }

    return (
        <div style={{
            height: windowSize[1] - navBarHeight,
            overflow: 'auto',
        }}>
            <AppBar 
                position='sticky'
                elevation={0}
                style={{
                    background: currentTheme.palette.background.paper,
                    height: appBarHeight,
                }}
            >
                <Toolbar style={{
                    justifyContent: 'left',
                }}>
                    <Typography
                        noWrap
                        style={{
                            marginLeft: '30px',
                            color: currentTheme.palette.text.primary,
                            
                        }}
                    >
                        Vessels
                    </Typography>
                </Toolbar>
            </AppBar>
            <div
                style={{
                    overflowX: 'auto',
                    position: 'relative',
                    padding: '10px',
                }}
            >
                {(userContext.currentUser?.access.includes('vesselManagerRead') ?? false)
                    ? (
                        <>
                            <Button
                                onClick={(): void => setShowAddNewVesselDialog(true)}
                                color='primary'
                                variant='contained'
                                disabled={!(userContext.currentUser?.access.includes('vesselManagerWrite') ?? false)}
                                style={{
                                    minWidth: '120px',
                                    maxWidth: '120px',
                                    minHeight: '32px',
                                    maxHeight: '32px',
                                    lineHeight: 'normal',
                                }}
                            >
                                New Vessel
                            </Button>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                Vessel Name
                                            </TableCell>
                                            <TableCell>
                                                IMO Number
                                            </TableCell>
                                            <TableCell>
                                                Company
                                            </TableCell>
                                            <TableCell>
                                                Vessel Type
                                            </TableCell>
                                            <TableCell>
                                                Built Date
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableBody}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <AddNewVesselDialog
                                controller={showAddNewVesselDialog}
                                onClose={handleCloseAddNewVesselDialog}
                                existingVessels={existingVessels}
                                vesselTypes={vesselTypes}
                                companies={companies}
                            />

                            <ExistingVesselInfoDialog
                                controller={showExistingVesselInfoDialog !== null}
                                onClose={(): void => setShowExistingVesselInfoDialog(null)}
                                vessel={showExistingVesselInfoDialog}
                                vesselTypes={vesselTypes}
                                companies={companies}
                            />

                            <LoadDataBackdrop
                                dataIsLoaded={vesselsIsLoaded && vesselTypesIsLoaded && companiesIsLoaded}
                                zIndex={1000}
                            />
                        </>
                    )
                    : (
                        <div style={{
                            textAlign: 'center',
                        }}>
                            <span style={{
                                color: currentTheme.palette.text.primary,
                            }}>
                                You do not have access to use the Vessel Manager.
                            </span>
                        </div>
                    )
                }
                
            </div>
        </div>
    );
}

export default VesselsTab;